import { useMemo } from 'react';
import { AccountCategory } from '../../state';
import {
  CashInfoByCurrency,
  Currency,
  InstrumentCategoryEnum,
  OrderExchangeCondition,
  OrderExchangeConditonId,
  OrderExchangeFundingData,
  OrderType,
  SelectedAccount,
  TradeType,
} from '../../types';
import { isDefined, Loadable, LoadableState } from '../../utils';
import { OpenOrder } from '../open-orders';
import { OrderFormData } from '../schemas';

export type UseOrderExchangeConditionsCommonParams = {
  funding: LoadableState<OrderExchangeFundingData>;
  cashInfo: LoadableState<CashInfoByCurrency>;
  selectedAccount: SelectedAccount | undefined;
  orderForm: OrderFormData | undefined;
  openOrder: OpenOrder | undefined;
};

export type UseOrderExchangeConditionsOutput = {
  conditions: LoadableState<OrderExchangeCondition[]>;
};

type LoadableConditions = Partial<Record<OrderExchangeConditonId, OrderExchangeCondition>>;

const ExcludedAccountCategories: AccountCategory[] = [AccountCategory.Nyesz];
const ExcludedInstrumentCategories: InstrumentCategoryEnum[] = [InstrumentCategoryEnum.Futures];
const AllowedOrderTypes: OrderType[] = ['limit', 'stopLimit', 'oco'];
const AllowedTradeTypes: TradeType[] = ['buy'];

export const useOrderExchangeConditionsCommon = ({
  cashInfo,
  funding,
  selectedAccount,
  orderForm: order,
  openOrder,
}: UseOrderExchangeConditionsCommonParams): UseOrderExchangeConditionsOutput => {
  const modifiedTotalNotReduced = useMemo<LoadableState<OrderExchangeCondition> | undefined>(() => {
    // We are not modifying an existing order, nothing to do here
    if (!isDefined(openOrder)) {
      return undefined;
    }
    // We are modifying an order that has no price (bought at market price for example), nothing to do here.
    if (!isDefined(openOrder.price)) {
      return undefined;
    }
    // Currency exchange only applies to buy orders
    if (openOrder.tradeType !== TradeType.Buy) {
      return undefined;
    }
    return Loadable.map(funding, (funding): OrderExchangeCondition => {
      const openOrderCost = (openOrder.price ?? Infinity) * openOrder.quantity + (openOrder.commission ?? 0);
      const editedOrderCost = funding.total.total;
      return {
        id: 'modifiedTotalNotReduced',
        isSatisfied: openOrderCost < editedOrderCost,
      };
    });
  }, [funding, openOrder]);

  const hasOtherCurrencyBalance = useMemo<LoadableState<OrderExchangeCondition>>(() => {
    return Loadable.map(cashInfo, (cashInfo): OrderExchangeCondition => {
      // Currencies that are not order.instrument.currency and they are not zero.
      const useableCurrencies = (Object.keys(cashInfo.cashInfo) as Currency[])
        .filter(currency => currency !== order?.instrument?.currency)
        .filter(currency => cashInfo.cashInfo[currency].investable > 0);
      return {
        id: 'hasOtherCurrencyBalance',
        isSatisfied: isDefined(order?.instrument) && useableCurrencies.length > 0,
      };
    });
  }, [cashInfo, order?.instrument]);

  const accountNotExcluded = useMemo<OrderExchangeCondition>(() => {
    return {
      id: 'accountNotExcluded',
      isSatisfied:
        isDefined(selectedAccount) && !ExcludedAccountCategories.includes(selectedAccount.account.accountCategory),
    };
  }, [selectedAccount]);

  const instrumentNotExcluded = useMemo<OrderExchangeCondition>(() => {
    return {
      id: 'instrumentNotExcluded',
      isSatisfied:
        isDefined(order) &&
        isDefined(order.instrument) &&
        !ExcludedInstrumentCategories.includes(order.instrument.instrumentCategory as InstrumentCategoryEnum),
    };
  }, [order]);

  const orderTypeEligible = useMemo<OrderExchangeCondition>(() => {
    return {
      id: 'orderTypeEligible',
      isSatisfied: isDefined(order) && isDefined(order.orderType) && AllowedOrderTypes.includes(order.orderType),
    };
  }, [order]);

  const orderSideIsBuy = useMemo<OrderExchangeCondition>(() => {
    return {
      id: 'orderTradeTypeIsBuy',
      isSatisfied: isDefined(order) && isDefined(order.tradeType) && AllowedTradeTypes.includes(order.tradeType),
    };
  }, [order]);

  // Collect all Loadable conditions in a record.
  // This is done, so that we have a consistent object shape - and never have to deal with Loadable.loaded(undefined)
  const loadableConditions = useMemo<LoadableState<LoadableConditions>>(() => {
    if (isDefined(modifiedTotalNotReduced)) {
      return Loadable.merge(
        [hasOtherCurrencyBalance, modifiedTotalNotReduced],
        (hasOtherCurrencyBalance, modifiedTotalNotReduced): LoadableConditions => ({
          hasOtherCurrencyBalance,
          modifiedTotalNotReduced,
        }),
      );
    }
    return Loadable.map(
      hasOtherCurrencyBalance,
      (hasOtherCurrencyBalance): LoadableConditions => ({ hasOtherCurrencyBalance }),
    );
  }, [hasOtherCurrencyBalance, modifiedTotalNotReduced]);

  return useMemo<UseOrderExchangeConditionsOutput>(() => {
    const conditions = Loadable.map(
      loadableConditions,
      ({ hasOtherCurrencyBalance, modifiedTotalNotReduced }): OrderExchangeCondition[] => [
        ...(isDefined(modifiedTotalNotReduced) ? [modifiedTotalNotReduced] : []),
        ...(isDefined(hasOtherCurrencyBalance) ? [hasOtherCurrencyBalance] : []),
        accountNotExcluded,
        instrumentNotExcluded,
        orderTypeEligible,
        orderSideIsBuy,
      ],
    );

    return { conditions };
  }, [accountNotExcluded, instrumentNotExcluded, loadableConditions, orderSideIsBuy, orderTypeEligible]);
};
